<template>
  <div
    class="variable-mapping-panel"
    :class="{
      'variable-mapping-panel--drawer': layout === 'drawer',
      'variable-mapping-panel--admin': layout === 'admin',
    }"
  >
    <!-- <div v-if="modeLabel" class="variable-mapping-badge">
      Data source: {{ modeLabel }}
    </div> -->
    <b-alert v-if="error" show variant="warning" class="small py-2 mb-2">
      {{ error }}
    </b-alert>
    <b-spinner v-if="loading" small class="mb-2" label="Loading catalog" />
    <div
      class="mapping-table-wrap mapping-table-wrap--table-bar"
      :class="{ 'mapping-table-wrap--vizlet': context === 'vizlet' }"
    >
      <div class="mapping-table-search-bar">
        <div
          v-if="mode === 'table' && qualifiedTableLabel"
          class="mapping-table-selection"
        >
          <span
            class="mapping-table-selection__chip"
            :title="qualifiedTableLabel"
          >{{ qualifiedTableLabel }}</span>
        </div>
        <b-form-input
          :value="searchQuery"
          type="search"
          size="sm"
          class="mapping-table-search-input"
          placeholder="Search columns…"
          aria-label="Search mapping table columns"
          @input="onSearchInput"
        />
      </div>
      <div class="mapping-table-scroll-body">
        <TableVariableMappingTable
          v-if="mode === 'table'"
          :rows="displayRows"
          :compact="layout === 'drawer'"
          :show-seq-column="effectiveShowSeqColumn"
          :show-graph-column="effectiveShowGraphColumn"
          :show-filter-column="effectiveShowFilterColumn"
          :sort-state="sortState"
          @row-change="onRowChange"
          @set-all-include="onSetAllInclude"
          @sort-toggle="onSortToggle"
        >
          <template #empty>
            <slot name="table-empty">Select a table or check datasql parses schema.table.</slot>
          </template>
        </TableVariableMappingTable>
        <SqlVariableMappingTable
          v-else-if="mode === 'sql'"
          :rows="displayRows"
          :compact="layout === 'drawer'"
          :show-column="effectiveShowColumn"
          :show-required-column="context !== 'vizlet'"
          :show-filter-column="effectiveShowFilterColumn"
          :sort-state="sortState"
          @row-change="onRowChange"
          @sort-toggle="onSortToggle"
        >
          <template #empty>
            <slot name="sql-empty">Enter or parse SQL to load column mapping.</slot>
          </template>
        </SqlVariableMappingTable>
      </div>
    </div>
  </div>
</template>

<script>
import TableVariableMappingTable from "./TableVariableMappingTable.vue";
import SqlVariableMappingTable from "./SqlVariableMappingTable.vue";
import {
  cycleMappingSortState,
  sortMappingRows,
} from "@/common/mappingTableSort";
import { resolveSqlMappingRowGraph } from "@/common/columnMappingCore";

export default {
  name: "VariableMappingPanel",
  components: { TableVariableMappingTable, SqlVariableMappingTable },
  props: {
    mode: {
      type: String,
      default: null,
      validator: (v) => v == null || v === "sql" || v === "table",
    },
    rows: { type: Array, default: () => [] },
    searchQuery: { type: String, default: "" },
    loading: { type: Boolean, default: false },
    error: { type: String, default: "" },
    context: {
      type: String,
      default: "admin",
      validator: (v) => v === "admin" || v === "vizlet",
    },
    layout: {
      type: String,
      default: "admin",
      validator: (v) => v === "admin" || v === "drawer",
    },
    showGraphColumn: { type: Boolean, default: true },
    showModeBadge: { type: Boolean, default: true },
    showFilterColumn: { type: Boolean, default: true },
    showSeqColumn: { type: Boolean, default: true },
    /** Vizlet table mode: checked rows at top (chip order when visibleColumns set). */
    prioritizeIncludedRows: { type: Boolean, default: false },
    visibleColumns: { type: Array, default: () => [] },
    /** reportHeaderColumnDp — used to derive Graph=Yes when label# → graphN exists */
    headerMap: { type: Object, default: () => ({}) },
    tableSchema: { type: String, default: "" },
    tableName: { type: String, default: "" },
  },
  data() {
    return {
      sortState: [],
    };
  },
  computed: {
    effectiveShowFilterColumn() {
      if (this.context === "vizlet") return false;
      return this.showFilterColumn;
    },
    effectiveShowGraphColumn() {
      if (this.context === "vizlet") return true;
      return this.showGraphColumn;
    },
    effectiveShowSeqColumn() {
      if (this.context === "vizlet") return false;
      return this.showSeqColumn;
    },
    /** Vizlet SQL mode: Show checkbox toggles visible column chips */
    effectiveShowColumn() {
      return this.context === "vizlet" && this.mode === "sql";
    },
    sortedRows() {
      const prioritizeIncluded =
        this.prioritizeIncludedRows &&
        (this.mode === "table" || this.mode === "sql");
      return sortMappingRows(this.rows, this.sortState, this.mode, {
        prioritizeIncluded,
        visibleColumns: this.visibleColumns,
      });
    },
    displayRows() {
      const map = this.headerMap || {};
      const hasHeaderMap =
        map && typeof map === "object" && Object.keys(map).length > 0;
      if (!hasHeaderMap) {
        return this.sortedRows;
      }
      if (this.mode === "sql") {
        return this.sortedRows.map((row) => {
          if (String(row.type || "").trim() !== "Metric") return row;
          const graph = resolveSqlMappingRowGraph(map, {
            label: row.label,
            headerDisplayKey: row._headerDisplayKey || row.label,
            metricAlias: row._metricAlias || row._aliasKey,
            aliasKey: row._aliasKey,
            prevGraph: row.graph,
          });
          if (graph === row.graph) return row;
          return { ...row, graph };
        });
      }
      if (this.mode === "table") {
        return this.sortedRows.map((row) => {
          if (row.displayType !== "Metric") return row;
          const graph = resolveSqlMappingRowGraph(map, {
            label: row.displayName,
            headerDisplayKey: row._headerDisplayKey || row.displayName,
            metricAlias: row._metricAlias || row._aliasKey,
            aliasKey: row._aliasKey,
            prevGraph: row.graph,
          });
          if (graph === row.graph) return row;
          return { ...row, graph };
        });
      }
      return this.sortedRows;
    },
    modeLabel() {
      if (!this.showModeBadge || !this.mode) return "";
      return this.mode === "table" ? "Table" : "SQL";
    },
    qualifiedTableLabel() {
      const schema = String(this.tableSchema || "").trim();
      const name = String(this.tableName || "").trim();
      if (!schema && !name) return "";
      if (schema && name) return `${schema}.${name}`;
      return schema || name;
    },
  },
  watch: {
    mode() {
      this.sortState = [];
    },
  },
  methods: {
    onSearchInput(value) {
      this.$emit("update:searchQuery", value);
    },
    onRowChange(payload) {
      this.$emit("row-change", payload);
    },
    onSetAllInclude(include) {
      this.$emit("set-all-include", include);
    },
    onSortToggle(field) {
      this.sortState = cycleMappingSortState(this.sortState, field);
    },
  },
};
</script>

<style src="./variable-mapping.css"></style>
